/** Journey definitions (reference/journeys/*.yaml) + graph helpers the wizard needs client-side. */
import { parse } from "yaml";
import { descriptors } from "@kit/descriptors/index";
import { edgeTypes, factTypes } from "@kit/spines";
import { picklists } from "@kit/picklists";

export interface StepDef {
  key: string; ask: string; mode: string;
  bind?: string; creates?: string; many?: boolean; for_each?: string;
  links?: { relation: string; to: string }; options_from?: string;
  assign?: string; prefill?: Record<string, string>; dedupe_on?: Record<string, string>;
  segment?: { of: string; assign: string; edge?: string };
  tree?: { parent: string }; copyable?: boolean;
}
export interface JourneyDef { journey: string; title: string; frameworks: string[]; reuses?: string[]; phases: Array<{ key: string; title: string; steps: StepDef[]; gate?: { tables: string[]; emits?: string } }> }

const raw = import.meta.glob("../../reference/journeys/*.yaml", { query: "?raw", import: "default", eager: true }) as Record<string, string>;
export const journeyDefs: Record<string, JourneyDef> = Object.fromEntries(Object.values(raw).map((t) => parse(t) as JourneyDef).map((j) => [j.journey, j]));

export const stepOf = (j: JourneyDef, phase: string, step: string) => j.phases.find((p) => p.key === phase)?.steps.find((s) => s.key === step);

const slugToFact = Object.fromEntries(Object.entries(factTypes).map(([k, n]) => [n.slug, k]));
const descriptorOf = (fact: string) => descriptors.find((d) => d.slug === factTypes[fact]?.slug);

/** Descriptor field metadata behind a `fact.field` bind. */
export function fieldOf(bind: string) {
  const [fact, field] = bind.split(".");
  return descriptorOf(fact)?.fields.find((f) => f.name === field);
}

/** For a `fact.relation` bind (pick modes): the fact type of the other end. */
export function bindTarget(bind: string): string | undefined {
  const [fact, field] = bind.split(".");
  const rel = descriptorOf(fact)?.relations?.find((r) => r.name === field);
  return rel ? slugToFact[rel.target] ?? rel.target : undefined;
}

/** Closed picklist behind a `fact.field` bind, if any. */
export function picklistOf(bind: string): string[] | undefined {
  const [fact, field] = bind.split(".");
  return picklists[factTypes[fact]?.slug ?? ""]?.[field];
}

export interface GraphNode { id: string; type: string; fields: Record<string, unknown> }
export function nodeLabel(n: GraphNode): string {
  const f = n.fields;
  const person = [f.firstname, f.lastname].filter(Boolean).join(" ");
  for (const v of [f.name, f.label, f.title, person, f.processingname, f.thirdpartiesname, f.assetname, f.data_category_name, f.threatsmeasurename])
    if (typeof v === "string" && v.trim()) return v;
  return n.id;
}

/** Catalogue entry -> {value, label} as the engine matches it (any field value identifies it). */
export const catalogOption = (e: Record<string, unknown>) => ({
  value: String(e.code ?? e.id ?? e.key ?? e.value),
  label: String(e.label ?? e.name ?? e.fullName ?? e.code),
});

/** Segment builder vocabulary: the person's own fields, and its edges (has a link / any linked record's name). */
export function segmentPaths(of: string): Array<{ value: string; label: string; kind: "field" | "has" | "any" }> {
  const d = descriptorOf(of);
  const fields = (d?.fields ?? []).map((f) => ({ value: `${of}.${f.name}`, label: f.label ?? f.name, kind: "field" as const }));
  const edges = edgeTypes.filter((e) => e.from === of).flatMap((e) => {
    const target = descriptorOf(e.to)?.fields;
    const name = target?.find((f) => f.name === "name")?.name ?? target?.find((f) => f.type === "text" && f.required)?.name;
    return [
      { value: `${of}.has.${e.relation}`, label: `${e.label} (almeno uno)`, kind: "has" as const },
      ...(name ? [{ value: `${of}.any.${e.relation}.${name}`, label: `${e.label}: ${name.replace(/_/g, " ")}`, kind: "any" as const }] : []),
    ];
  });
  return [...edges, ...fields];
}

/** Fact types a journey may need as pick candidates or labels. */
export function typesOf(j: JourneyDef): string[] {
  const out = new Set<string>();
  for (const p of j.phases) for (const s of p.steps) {
    if (s.links) out.add(s.links.to);
    if (s.for_each) out.add(s.for_each);
    if (s.creates) out.add(s.creates);
    if (s.segment) out.add(s.segment.of);
    if (s.bind) { const t = bindTarget(s.bind); if (t) out.add(t); }
  }
  return [...out];
}

/** Kernel records of the given fact types as graph nodes (the engine's base stays server-side). */
export async function loadNodes(types: string[]): Promise<GraphNode[]> {
  const { kernel } = await import("./api");
  const lists = await Promise.all(types.map(async (type) => {
    const slug = factTypes[type]?.slug;
    if (!slug) return [];
    const r = await kernel<{ entities: Array<{ entity_id: string; label: string; metadata: Record<string, unknown> | null }> }>(
      `/entities?type=${encodeURIComponent(slug)}&limit=1000`).catch(() => ({ entities: [] }));
    return r.entities.map((e) => ({ id: e.entity_id, type, fields: { label: e.label, ...(e.metadata ?? {}) } }));
  }));
  return lists.flat();
}
